import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import InvestigationStepContainer from './InvestigationStepContainer';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import Column from '../../../common/layout/Column';
import BooleanSelect from '../common/BooleanSelect';
import DateInput from '../../../common2/DateInput';
import Uploader from '../../../common/Uploader';
import ClaimStepClosableMessage from '../common/ClaimStepClosableMessage';
import Locale from '../../../Translate/Locale';
import SidePanelContentHeader from '../../../common2/side-panel/SidePanelContentHeader';
import InputHelpLink from '../InputHelpLink';
import CustomerCommentInput from '../common/CustomerCommentInput';
import useOnFormChange from '../../../../hooks/useOnFormChange';
import { useYupValidation } from '../../../../hooks/useYupValidation';
import FileImportMessage from '../common/FileImportMessage';
import Loader from '../../../common/Loader';
import { ClaimFileType } from '../types';

function InvestigationDeliveryInfoStep(props) {
  const { claim, onChange, onNext, importDORState, ...restProps } = props;
  const { declarationOfRecipient, isDelivered, shipmentType } = claim;
  const [isNextBtnDisabled, setIsNextBtnDisabled] = useState(false);

  const onInputChange = useOnFormChange(onChange);

  const deliveryDateInputName = claim.postalReturnDeliveryDate ? 'postalReturnDeliveryDate' : 'deliveryDate';
  const deliveryDateLabel = shipmentType === 'outbound' ? 'Delivery date' : "Delivery date at carrier's hub";
  const schema = createValidationSchema({ deliveryDateInputName });

  const { validate, errors } = useYupValidation(schema);

  const onClickNext = () => {
    if (validate(claim)) {
      onNext();
    }
  };

  const uploaderHelpLinkElement = (
    // eslint-disable-next-line max-len
    <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Declaration-of-Receipt-(DOR)-Requirements%C2%A0">
      Declaration of receipt requirements
    </InputHelpLink>
  );

  if (importDORState === 'searching') {
    return (
      <Container isDisabled>
        <Loader loadingText="CLAIMS_IMPORT_DOR_SEARCHING" />
      </Container>
    );
  }

  return (
    <InvestigationStepContainer {...restProps} onNext={onClickNext} isDisabled={isNextBtnDisabled}>
      <FileImportMessage state={importDORState} fileType={ClaimFileType.DECLARATION_OF_RECIPIENT} />
      <SidePanelContentHeader>Delivery information</SidePanelContentHeader>
      <Column spacing="medium">
        <ColumnsLayout>
          <BooleanSelect name="isDelivered" label="Delivery successful" value={isDelivered} isDisabled />
          <DateInput
            name={deliveryDateInputName}
            label={deliveryDateLabel}
            placeholder="Add date..."
            isRequired
            disabled
            value={claim[deliveryDateInputName]}
            error={errors[deliveryDateInputName]}
          />
        </ColumnsLayout>
        <CustomerCommentInput claim={claim} onChange={onInputChange} />
        {!isDelivered ? (
          <ClaimStepClosableMessage intent="success">
            <Locale>CLAIMS_NO_ACTION_REQUIRED_CLICK_NEXT</Locale>
          </ClaimStepClosableMessage>
        ) : (
          <Uploader
            name="declarationOfRecipient"
            label="Declaration of recipient"
            topRightElement={uploaderHelpLinkElement}
            isRequired
            isDisabled={!isDelivered}
            files={declarationOfRecipient}
            errorWithTranslation={errors.declarationOfRecipient}
            allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
            helpText="CLAIMS_DECLARATION_OF_RECIPIENT_UPLOAD_HELP_TEXT"
            onChange={onInputChange}
            onBusy={setIsNextBtnDisabled}
          />
        )}
      </Column>
    </InvestigationStepContainer>
  );
}

function createValidationSchema(config) {
  const { deliveryDateInputName } = config;

  return yup.object().shape({
    isDelivered: yup.boolean().required(),
    [deliveryDateInputName]: yup
      .date()
      .nullable()
      .when('isDelivered', {
        is: true,
        then: (schema) => schema.required(),
      })
      .label('Delivery date'),
    declarationOfRecipient: yup
      .array()
      .required()
      .when('isDelivered', {
        is: true,
        then: (schema) => schema.min(1),
      })
      .label('Declaration of recipient'),
  });
}

InvestigationDeliveryInfoStep.propTypes = {
  claim: PropTypes.object,
  onChange: PropTypes.func,
};

export default React.memo(InvestigationDeliveryInfoStep);
